import React from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import Button from "../../components/buttons/Button";
import type { UploadSession } from "../types";
import { sessionFailed as copy, interpolate } from "../copy";

interface SessionFailedBannerProps {
  session: UploadSession;
  canResubmit: boolean;
  onResubmit: () => void;
  counts: { errors: number; total: number };
}

const SessionFailedBanner: React.FC<SessionFailedBannerProps> = ({
  session,
  canResubmit,
  onResubmit,
  counts,
}) => {
  const vars = { rejected_count: counts.errors, total_items: counts.total };
  return (
    <Box
      sx={{
        minHeight: "72px",
        padding: "16px 20px 16px 60px",
        background: "linear-gradient(135deg, #FFF1EE 0%, #FFE5DD 100%)",
        border: "1px solid rgba(211,47,47,0.18)",
        borderRadius: "16px",
        boxShadow: "0 2px 8px -4px rgba(211,47,47,0.20)",
        position: "relative",
        mb: 1,
      }}
    >
      {/* Pulsing icon */}
      <Box
        sx={{
          position: "absolute",
          top: "18px",
          left: "20px",
          "& .pulse-ring": {
            position: "absolute",
            inset: "-4px",
            borderRadius: "50%",
            animation: "sessionFailedPulse 1.6s ease-out 1 forwards",
          },
          "@keyframes sessionFailedPulse": {
            "0%": { boxShadow: "0 0 0 0 rgba(211,47,47,0.3)" },
            "100%": { boxShadow: "0 0 0 8px rgba(211,47,47,0)" },
          },
        }}
      >
        <Box className="pulse-ring" />
        <Icon icon="mdi:alert-octagon" color="#D32F2F" width={28} />
      </Box>

      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
      >
        <Stack spacing={0.5}>
          {session.error_message && (
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#5C0A0A", lineHeight: "20px" }}
            >
              {interpolate(session.error_message, vars)}
            </Typography>
          )}
          {session.action_item && (
            <Typography
              variant="body2"
              sx={{ fontWeight: 400, color: "#7A2222", lineHeight: "18px" }}
            >
              {interpolate(session.action_item, vars)}
            </Typography>
          )}
        </Stack>

        <Box sx={{ flexShrink: 0 }}>
          <Tooltip
            title={!canResubmit ? copy.resubmitDisabledTooltip : ""}
            placement="left"
          >
            <span>
              <Button
                onClick={onResubmit}
                disabled={!canResubmit}
                variant="text"
                size="small"
                width="auto"
                sx={{
                  color: "#D32F2F",
                  fontWeight: 500,
                  "&:disabled": { color: "rgba(211,47,47,0.4)" },
                }}
              >
                {copy.resubmit}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Stack>
    </Box>
  );
};

export default SessionFailedBanner;
